import AdAudienceModel from './AdAudienceModel';
import { ConnectionTagModel } from './ConnectionTagModel';
import { ConnectionVariableModel } from './ConnectionVariableModel';
import { SalesForceAccount } from './SalesForceModel';
import { SendlaneAccessToken } from './SendlaneModel';
import { SlackAccount } from './SlackModel';
import { SendDataConfig } from './WidgetModel';

export type ConnectionModelType = 'Webhook';

export enum ConnectionRequestType {
  GET = 'get',
  POST = 'post',
}

export enum ConnectionResponseType {
  NONE = 'none',
  SAVE_ATTRIBUTES = 'save_attributes',
  MESSENGER_JSON = 'messenger_json',
}

export enum ConnectionKind {
  AUDIENCE = 'variables_updates',
  WIDGET = 'widget',
}

export type IntegrationTypes =
  | 'sendlane'
  | 'klaviyo'
  | 'default'
  | 'google_spreadsheet'
  | 'facebook_ads_audience'
  | 'google_ads_audience'
  | 'mailchimp'
  | 'salesforce'
  | 'gohighlevel'
  | 'slack'
  | 'leadconnector'
  | 'shopify'
  | 'capi'
  | string
  | null;

export type WebhookKind =
  | 'gohighlevel'
  | 'google_spreadsheet'
  | 'mailchimp'
  | 'facebook_ads_audience'
  | 'zapier'
  | 'json'
  | 'klaviyo'
  | 'salesforce'
  | 'sendlane'
  | 'slack'
  | 'shopify';

export type ConnectionDataType = {
  listId?: string;
  listName?: string;
  locationId?: string;
  name?: string;
  skipName?: boolean;
  emailVariableId?: number;
  messageInfo?: SlackMessageInfo;
};

export type SlackMessageInfo = {
  content?: string;
  sendAsBot?: boolean;
  assetUrl?: string | null;
  botName?: string;
};

export type FormattedConnectionModel = ConnectionModel & {
  statusText: string;
  formattedUpdatedAt: string;
  formattedCreatedAt: string;
};

export type ActiveCRMSResponse = {
  crmConnections: string[];
};

export type WebhookJsonTemplateModel = {
  imageUrl: string;
  title: string;
  url: string;
  requestType: ConnectionRequestType;
  requestHeaders: string;
  requestBody: string;
  sortOrder: number;
};

type KlaviyoParams = {
  oauthTokenId?: number;
  accessTokenId?: number;
};

type TokenError = {
  code?: number;
  message?: string;
};

export type TokenErrorInfo = string | TokenError | null;

export type KlaviyoAccessToken = {
  id: number;
  errorInfo: TokenErrorInfo;
  status: 'initial' | 'active' | 'error';
  privateKey: string;
  readOnly: boolean;
};

type SendlaneParams = {
  accessTokenId?: number;
};

export type KlaviyoOauthToken = AccessToken & {
  requiredScopes: string[];
  missingScopes: string[];
};

type GoHighLevelLocationToken = {
  errorInfo: string | null;
  remoteLocationId: string;
};

export type GoHighLevelOauthToken = {
  id: number;
  remoteLocationId: string;
  errorInfo: TokenErrorInfo;
  requiredScopes: string[];
  missingScopes: string[];
  locationTokens: GoHighLevelLocationToken[] | null;
  readOnly: boolean;
};

export type AccessToken = {
  id: number;
  errorInfo: TokenErrorInfo;
  status: 'active' | string;
  readOnly: boolean;
};

export type ShopifyOauthToken = AccessToken & {
  shop: string;
  type: 'Shopify::OauthToken';
};

export type ShopifyAccessToken = AccessToken & {
  shop: string;
  accessToken: string;
  type: 'Shopify::AccessToken';
};

export type WebhookError = {
  updatedAt: string;
  errorDetails: string;
  quantity: number;
};

export const tokenErrorMsg = (
  tokenType: 'apiKey' | 'oauthToken',
  errorInfo: TokenErrorInfo,
  defaultMessage?: string,
): string => {
  let message: string | undefined;

  const token = tokenType === 'apiKey' ? 'API key' : 'OAuth token';

  if (errorInfo) {
    message = typeof errorInfo === 'string' ? errorInfo : errorInfo.message;
  }
  if (message) {
    if (message.match('missing required scopes')) {
      return `Your ${token} is missing required permissions.`;
    }
    if (message.match('Incorrect authentication credentials')) {
      return `Your ${token} is invalid, please update it.`;
    }
    return message;
  }
  return defaultMessage || `${token} error, please update it.`;
};

export type ConnectionModel = {
  id: number;
  name: string;
  connectionType: ConnectionModelType;
  description: string;
  createdAt: string;
  updatedAt: string;
  pluginId: number;
  validationMessages: any;
  accessToken?: AccessToken;
  url: string;
  variables?: ConnectionVariableModel[];
  audienceId: number | null;
  active: boolean;
  sendDataWidgetConfig?: SendDataConfig;
  requestType: ConnectionRequestType;
  responseType: ConnectionResponseType;
  kind: ConnectionKind;
  tags: ConnectionTagModel[];
  tagsField: string | null;
  googleAccountId: number | null;
  googleAccountEmail: string | null;
  googleLookupColumn: string | null;
  googleSheetId: string | null;
  googleSheetTitle: string | null;
  googleSpreadsheetId: string | null;
  googleSpreadsheetTitle: string | null;
  integrationType: IntegrationTypes;
  connectionKind: WebhookKind;
  mailchimpAccountId: number | null;
  mailchimpAccountEmail: string | null;
  mailchimpAccessTokenId: number | null;
  data: ConnectionDataType;
  mailchimpListTitle: string | null;
  requestHeaders?: Record<any, any> | string | null;
  requestBody?: Record<any, any> | string | null;
  klaviyoParams?: KlaviyoParams;
  klaviyoAccessToken?: KlaviyoAccessToken;
  klaviyoOauthToken?: KlaviyoOauthToken;
  gohighlevelOauthToken?: GoHighLevelOauthToken;
  sendlaneParams?: SendlaneParams;
  sendlaneAccessToken?: SendlaneAccessToken;
  salesforceAccessToken?: SalesForceAccount;
  promoterIds?: number[];
  adAudienceId?: number;
  adAudience?: AdAudienceModel;
  webhookErrors?: WebhookError[];
  salesforceParams?: {
    accessTokenId: number;
  };
  gohighlevelParams?: {
    oauthTokenId: number;
  };
  slackAccessToken?: SlackAccount;
  slackParams?: {
    accessTokenId?: number;
  };
  shopifyParams?: {
    tokenId?: number;
    tokenType?: 'Shopify::AccessToken' | 'Shopify::OauthToken';
    token?: {
      shop?: string;
      accessToken?: string;
    };
  };
  contactsSent?: number;
  showCounters?: boolean;
};

export default ConnectionModel;
